/**
 * React Query hooks for subscription management
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { subscriptionService } from '../services/subscription';
import type { ChangePlanData } from '../types';
import toast from 'react-hot-toast';

/**
 * Query key factory for subscription management
 */
export const subscriptionKeys = {
  all: ['subscription'] as const,
  plans: () => [...subscriptionKeys.all, 'plans'] as const,
  current: () => [...subscriptionKeys.all, 'current'] as const,
  usage: () => [...subscriptionKeys.all, 'usage'] as const,
  payments: () => [...subscriptionKeys.all, 'payments'] as const,
};

/**
 * Get all available subscription plans
 */
export function useGetAvailablePlans() {
  return useQuery({
    queryKey: subscriptionKeys.plans(),
    queryFn: () => subscriptionService.getAvailablePlans(),
    staleTime: 1000 * 60 * 10, // 10 minutes
  });
}

/**
 * Get current subscription details
 */
export function useGetCurrentSubscription() {
  return useQuery({
    queryKey: subscriptionKeys.current(),
    queryFn: () => subscriptionService.getCurrentSubscription(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get account usage statistics
 */
export function useGetUsage() {
  return useQuery({
    queryKey: subscriptionKeys.usage(),
    queryFn: () => subscriptionService.getUsage(),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

/**
 * Get payment history
 */
export function useGetPaymentHistory() {
  return useQuery({
    queryKey: subscriptionKeys.payments(),
    queryFn: () => subscriptionService.getPaymentHistory(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Change subscription plan
 */
export function useChangePlan() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: ChangePlanData) => subscriptionService.changePlan(data),
    onError: (error: any) => {
      const message = error.response?.data?.message || 'Failed to change subscription plan';
      toast.error(message);
    },
    onSuccess: (data) => {
      toast.success(data.message || 'Subscription plan changed successfully');
      // Invalidate all subscription-related queries
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.current() });
      queryClient.invalidateQueries({ queryKey: subscriptionKeys.usage() });
      // Also invalidate account details in case it's used elsewhere
      queryClient.invalidateQueries({ queryKey: ['account', 'detail'] });
    },
  });
}
